import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, MapPin } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/widgets/dropdown-menu';
import {
  PLP_DEFAULT_SORT,
  PLP_SORT_OPTIONS,
  type PlpSortByValue,
} from '@/features/plp/constants';

function sortLabelForValue(value: PlpSortByValue): string {
  const option = PLP_SORT_OPTIONS.find((o) => o.value === value);
  return option?.label ?? 'Popular';
}

export function PlpTopFilterBar({
  onActivateStoreDesigns,
  onShowAllDesigns,
  isStoreDesignsActive,
  pincode,
  onOpenPincode,
  onOpenSort,
  onApplySort,
  onSortHover,
  hidePincode = false,
  currentSortBy,
}: {
  onActivateStoreDesigns?: () => void;
  onShowAllDesigns?: () => void;
  isStoreDesignsActive?: boolean;
  pincode?: string;
  onOpenPincode?: () => void;
  onOpenSort?: () => void;
  onApplySort?: (sort: PlpSortByValue) => void;
  onSortHover?: () => void;
  hidePincode?: boolean;
  currentSortBy?: PlpSortByValue;
}) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close the sort dropdown when the user scrolls.
  useEffect(() => {
    if (!isSortOpen) return;
    const close = () => setIsSortOpen(false);
    window.addEventListener('scroll', close, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', close, { capture: true });
  }, [isSortOpen]);

  const openSort = () => {
    if (sortCloseTimer.current) clearTimeout(sortCloseTimer.current);
    setIsSortOpen(true);
    onSortHover?.();
  };
  const scheduleCloseSort = () => {
    sortCloseTimer.current = setTimeout(() => setIsSortOpen(false), 150);
  };

  const sortValue = currentSortBy ?? (PLP_DEFAULT_SORT as PlpSortByValue);
  const sortLabel = sortLabelForValue(sortValue).toUpperCase();
  const showDesktopPincode = Boolean(onOpenPincode) && !hidePincode;
  const showDesktopSortDropdown = Boolean(onApplySort);
  const showDesktopSortFallback = Boolean(onOpenSort) && !onApplySort;
  const showDesktopSort = showDesktopSortDropdown || showDesktopSortFallback;
  const showDesktopActions = showDesktopPincode || showDesktopSort;

  return (
    <section className="px-4 py-2">
      <div className="mx-auto flex w-full items-center justify-between gap-3">
        {(onShowAllDesigns || onActivateStoreDesigns) ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={[
                'rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wide',
                isStoreDesignsActive
                  ? 'border-2 border-white bg-transparent text-white'
                  : 'bg-white text-[#4A607F]',
              ].join(' ')}
              aria-pressed={!isStoreDesignsActive}
              onClick={onShowAllDesigns}
            >
              All
            </button>
            <button
              type="button"
              className={[
                'rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wide',
                isStoreDesignsActive
                  ? 'bg-white text-[#4A607F]'
                  : 'border-2 border-white bg-transparent text-white',
              ].join(' ')}
              aria-pressed={Boolean(isStoreDesignsActive)}
              onClick={onActivateStoreDesigns}
            >
              Designs in store
            </button>
          </div>
        ) : <div />}

        {showDesktopActions ? (
          <div className="hidden shrink-0 items-center gap-2 md:flex">
            {showDesktopPincode ? (
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md bg-white text-[#3f84ad] px-3 py-2 text-xs font-semibold uppercase tracking-wide"
                onClick={onOpenPincode}
              >
                <MapPin className="h-4 w-4 shrink-0" aria-hidden />
                <span>{pincode || 'Pincode'}</span>
              </button>
            ) : null}
            {showDesktopSortDropdown && onApplySort ? (
              <DropdownMenu modal={false} open={isSortOpen} onOpenChange={setIsSortOpen}>
                <DropdownMenuTrigger asChild onMouseEnter={openSort} onMouseLeave={scheduleCloseSort}>
                  <button
                    type="button"
                    className="inline-flex min-w-[8.5rem] items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#3F84AD] outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-[#3F84AD]/40"
                  >
                    <span className="truncate">{sortLabel}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-600" aria-hidden />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={6} className="min-w-[14rem]" onMouseEnter={openSort} onMouseLeave={scheduleCloseSort}>
                  {PLP_SORT_OPTIONS.map((option) => {
                    const selected = sortValue === option.value;
                    return (
                      <DropdownMenuItem
                        key={option.value}
                        className="flex cursor-pointer items-center justify-between gap-3 py-2.5 pl-3 pr-2 text-sm text-gray-900 focus:bg-gray-100"
                        onSelect={() => {
                          onApplySort(option.value);
                        }}
                      >
                        <span>{option.label}</span>
                        <Check
                          className={[
                            'h-4 w-4 shrink-0',
                            selected ? 'text-[#3F84AD]' : 'text-transparent',
                          ].join(' ')}
                          aria-hidden
                        />
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {showDesktopSortFallback && onOpenSort ? (
              <button
                type="button"
                className="inline-flex min-w-[8.5rem] items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-900"
                onClick={onOpenSort}
              >
                <span className="truncate">{sortLabel}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-600" aria-hidden />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}